import { getBuyLinkExpiryStatus } from "../buyLinkExpiry";

export type DashboardPeriod =
  | "TODAY"
  | "7D"
  | "MONTH"
  | "LAST_MONTH"
  | "CUSTOM";

export type DashboardStatusFilter =
  | "ALL"
  | "NORMAL"
  | "IN_PROGRESS"
  | "DELAYED"
  | "ERROR";

export type DashboardFilters = {
  period: DashboardPeriod;
  dateFrom: string;
  dateTo: string;
  corporation: string;
  manager: string;
  part: string;
  status: DashboardStatusFilter;
};

export type DashboardPaymentMeta = {
  loaded: boolean;
  truncated: boolean;
  total: number;
  dateFrom: string;
  dateTo: string;
};

type UnknownRecord = Record<string, any>;

export type DashboardStatusAmount = {
  count: number;
  amount: number;
};

export type DashboardFunnelStage = {
  key: string;
  label: string;
  count: number;
  previousConversionRate: number | null;
  dropOffCount: number | null;
};

export type DashboardActionItem = {
  key: string;
  severity: "URGENT" | "WARNING" | "INFO";
  scope: "CONTRACT" | "PAYMENT";
  title: string;
  count: number;
  amount: number;
  detail: string;
};

export type DashboardManagerRow = {
  manager: string;
  part: string;
  submittedContracts: number;
  completedContracts: number;
  paymentApprovedAmount: number;
  paymentOutstandingAmount: number;
  attentionCount: number;
};

export type DashboardTrendRow = {
  date: string;
  contractCompletedCount: number;
  paymentApprovedAmount: number;
};

export type DashboardExceptionRow = {
  key: string;
  category: "USER" | "SALES" | "SYSTEM" | "KCP" | "DATA";
  label: string;
  count: number;
};

export type IntegratedDashboardStats = {
  summary: {
    contractTargetCount: number;
    contractCompletionRate: number | null;
    submittedContractCount: number;
    completedContractCount: number;
    todayCompletedContractCount: number;
    stalledContractCount: number;
    paymentRequestCount: number;
    paymentRequestAmount: number;
    paymentApprovedCount: number;
    paymentApprovedAmount: number;
    paymentCancelledAmount: number;
    paymentNetApprovedAmount: number;
    paymentOutstandingCount: number;
    paymentOutstandingAmount: number;
    paymentExpiringTodayCount: number;
    paymentExpiringTodayAmount: number;
    paymentExpiredCount: number;
    paymentExpiredAmount: number;
  };
  contractStatus: Record<string, number>;
  paymentStatus: Record<string, DashboardStatusAmount>;
  paymentAging: Record<string, DashboardStatusAmount>;
  funnel: DashboardFunnelStage[];
  actions: DashboardActionItem[];
  managers: DashboardManagerRow[];
  trends: DashboardTrendRow[];
  exceptions: DashboardExceptionRow[];
  filterOptions: {
    corporations: string[];
    managers: string[];
    parts: string[];
  };
  diagnostics: {
    contractMissingBusinessNumberCount: number;
    paymentMissingBusinessNumberCount: number;
    amountInvariantMismatchCount: number;
    duplicatePaymentRequestIdCount: number;
    paymentMissingExpiryCount: number;
    paymentPartFilterAvailable: false;
  };
};

const SEOUL_OFFSET_MS = 9 * 60 * 60 * 1000;
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LINK_PAYMENT_TYPES = new Set(["BUYLINK", "STANDARD"]);

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === "object" ? (value as UnknownRecord) : {};

const pickString = (...values: unknown[]): string =>
  values.map((value) => String(value ?? "").trim()).find(Boolean) || "";

const pickFiniteAmount = (value: unknown): number => {
  const numberValue = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 0;
};

const formatDateKey = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getUTCDate()).padStart(2, "0")}`;

const shiftDateKey = (dateKey: string, days: number): string => {
  const timestamp = Date.parse(`${dateKey}T00:00:00Z`);
  if (!Number.isFinite(timestamp)) return "";
  return formatDateKey(new Date(timestamp + days * 24 * 60 * 60 * 1000));
};

export const toSeoulDateKey = (value: unknown): string => {
  let candidate = value;
  if (candidate && typeof candidate === "object") {
    const timestampLike = candidate as {
      toDate?: () => unknown;
      seconds?: number;
      _seconds?: number;
    };
    if (typeof timestampLike.toDate === "function") {
      try {
        candidate = timestampLike.toDate();
      } catch {
        return "";
      }
    } else {
      const seconds = Number(timestampLike.seconds ?? timestampLike._seconds);
      if (Number.isFinite(seconds)) candidate = seconds * 1000;
    }
  }

  if (candidate instanceof Date) {
    const timestamp = candidate.getTime();
    return Number.isFinite(timestamp)
      ? formatDateKey(new Date(timestamp + SEOUL_OFFSET_MS))
      : "";
  }

  if (typeof candidate === "number") {
    const milliseconds =
      Math.abs(candidate) < 1_000_000_000_000 ? candidate * 1000 : candidate;
    return Number.isFinite(milliseconds)
      ? formatDateKey(new Date(milliseconds + SEOUL_OFFSET_MS))
      : "";
  }

  const text = String(candidate ?? "").trim();
  const compact = /^(\d{4})(\d{2})(\d{2})\d{0,6}$/.exec(text);
  if (compact) {
    const dateKey = `${compact[1]}-${compact[2]}-${compact[3]}`;
    return DATE_KEY_PATTERN.test(dateKey) &&
      formatDateKey(new Date(`${dateKey}T00:00:00Z`)) === dateKey
      ? dateKey
      : "";
  }

  const calendarPrefix = /^(\d{4}-\d{2}-\d{2})(?:$|T)/.exec(text);
  if (calendarPrefix) {
    const dateKey = calendarPrefix[1];
    if (formatDateKey(new Date(`${dateKey}T00:00:00Z`)) !== dateKey) return "";
    if (text.length === 10) return dateKey;
  }

  const timestamp = Date.parse(text);
  return Number.isFinite(timestamp)
    ? formatDateKey(new Date(timestamp + SEOUL_OFFSET_MS))
    : "";
};

export const getDashboardDateRange = (
  period: DashboardPeriod,
  now: Date = new Date(),
  customFrom = "",
  customTo = ""
): { dateFrom: string; dateTo: string } => {
  const today = toSeoulDateKey(now);
  if (period === "CUSTOM") {
    return {
      dateFrom: DATE_KEY_PATTERN.test(customFrom) ? customFrom : today,
      dateTo: DATE_KEY_PATTERN.test(customTo) ? customTo : today,
    };
  }
  if (period === "TODAY") return { dateFrom: today, dateTo: today };
  if (period === "7D") {
    return { dateFrom: shiftDateKey(today, -6), dateTo: today };
  }
  const [year, month] = today.split("-").map(Number);
  if (period === "LAST_MONTH") {
    const firstOfThisMonth = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastOfPreviousMonth = shiftDateKey(firstOfThisMonth, -1);
    return {
      dateFrom: `${lastOfPreviousMonth.slice(0, 7)}-01`,
      dateTo: lastOfPreviousMonth,
    };
  }
  return { dateFrom: `${today.slice(0, 7)}-01`, dateTo: today };
};

export const getDashboardPaymentLoadRange = (
  range: { dateFrom: string; dateTo: string },
  now: Date = new Date()
): { dateFrom: string; dateTo: string } => {
  const today = toSeoulDateKey(now);
  const recentFrom = shiftDateKey(today, -29);
  const dateFrom = [range.dateFrom, recentFrom]
    .filter((value) => DATE_KEY_PATTERN.test(value))
    .sort()[0] || recentFrom;
  const dateToCandidates = [range.dateTo, today]
    .filter((value) => DATE_KEY_PATTERN.test(value))
    .sort();
  const dateTo = dateToCandidates[dateToCandidates.length - 1] || today;
  return { dateFrom, dateTo };
};

const isDateInRange = (dateKey: string, dateFrom: string, dateTo: string) =>
  Boolean(dateKey && (!dateFrom || dateKey >= dateFrom) && (!dateTo || dateKey <= dateTo));

const ageInDays = (dateKey: string, asOfDateKey: string): number | null => {
  if (!dateKey || !asOfDateKey) return null;
  const start = Date.parse(`${dateKey}T00:00:00Z`);
  const end = Date.parse(`${asOfDateKey}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, Math.floor((end - start) / (24 * 60 * 60 * 1000)));
};

export const normalizeBusinessNumberForDashboard = (value: unknown): string => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length === 10 ? digits : "";
};

const getContractBusinessNumber = (contract: UnknownRecord): string => {
  const finalSnapshot = asRecord(contract.finalSnapshot);
  const businessInfoSnapshot = asRecord(finalSnapshot.businessInfoSnapshot);
  return normalizeBusinessNumberForDashboard(
    pickString(
      contract.agentBizNo,
      contract.businessNumber,
      contract.bizNo,
      businessInfoSnapshot.businessNumber
    )
  );
};

const getPaymentBusinessNumber = (payment: UnknownRecord): string =>
  normalizeBusinessNumberForDashboard(payment.vendorBusinessNumber);

const getContractManager = (contract: UnknownRecord): string => {
  const salesReceipt = asRecord(contract.salesReceipt);
  const finalSnapshot = asRecord(contract.finalSnapshot);
  return pickString(
    salesReceipt.managerName,
    contract.salesRepName,
    finalSnapshot.salesRepName,
    contract.managerName,
    "미지정"
  );
};

const getPaymentManager = (payment: UnknownRecord): string =>
  pickString(payment.requestedByName, payment.createdBy, payment.updatedBy, "미지정");

const getContractPart = (contract: UnknownRecord): string => {
  const finalSnapshot = asRecord(contract.finalSnapshot);
  return pickString(
    contract.salesRepDepartmentPart,
    contract.salesPart,
    finalSnapshot.salesRepDepartmentPart,
    "미지정"
  );
};

const getContractCorporation = (contract: UnknownRecord): string => {
  const contractCompanyBranch = asRecord(contract.contractCompanyBranch);
  const finalSnapshot = asRecord(contract.finalSnapshot);
  const finalBranch = asRecord(finalSnapshot.contractCompanyBranch);
  const supplierInfo = asRecord(finalSnapshot.supplierInfoSnapshot);
  return pickString(
    contractCompanyBranch.corporationName,
    contractCompanyBranch.branchName,
    contract.companyBranchName,
    finalBranch.corporationName,
    finalBranch.branchName,
    supplierInfo.companyName,
    "미지정"
  );
};

const getPaymentCorporation = (payment: UnknownRecord): string =>
  pickString(payment.paymentCompanyName, "미지정");

const isDeletedContract = (contract: UnknownRecord): boolean =>
  String(contract.status || "").trim().toLowerCase() === "deleted";

const isPendingCoRepresentativeContract = (contract: UnknownRecord): boolean => {
  const status = String(contract.status || "").toLowerCase();
  const contractStatus = String(contract.contractStatus || "").toUpperCase();
  const workflow = asRecord(contract.coRepresentativeWorkflow);
  return (
    status.includes("pending_co_rep") ||
    contractStatus.includes("PENDING_CO_REP") ||
    (workflow.required === true &&
      (Number(workflow.pendingCount) > 0 ||
        !String(workflow.status || "").toUpperCase().includes("COMPLETED")))
  );
};

const isRejectedContract = (contract: UnknownRecord): boolean =>
  String(contract.status || "").toLowerCase() === "rejected" ||
  contract.rejected === true;

const isContractError = (contract: UnknownRecord): boolean =>
  Boolean(
    isRejectedContract(contract) ||
      contract.errorCode ||
      contract.errorMessage ||
      String(contract.status || "").toUpperCase().includes("ERROR")
  );

const isCompletedContract = (contract: UnknownRecord): boolean => {
  if (
    isDeletedContract(contract) ||
    isPendingCoRepresentativeContract(contract) ||
    isRejectedContract(contract)
  ) {
    return false;
  }
  const status = String(contract.status || "").toLowerCase();
  const contractStatus = String(contract.contractStatus || "").toUpperCase();
  return (
    status === "completed" ||
    status === "confirmed" ||
    contract.confirmed === true ||
    contractStatus === "COMPLETED" ||
    (Boolean(contract.completedAt || asRecord(contract.finalSnapshot).completedAt) &&
      hasSignatureEvidence(contract))
  );
};

const hasIdentityEvidence = (contract: UnknownRecord): boolean => {
  const verification = asRecord(
    contract.representativeIdentityVerification ||
      asRecord(contract.finalSnapshot).representativeIdentityVerification
  );
  const status = pickString(
    verification.status,
    contract.identityVerificationStatus,
    asRecord(contract.identityVerification).status
  ).toUpperCase();
  return ["MATCHED", "VERIFIED", "FALLBACK_VERIFIED"].includes(status);
};

const hasSignatureEvidence = (contract: UnknownRecord): boolean => {
  const finalSnapshot = asRecord(contract.finalSnapshot);
  const signatureEvidence = asRecord(
    contract.signatureEvidence || finalSnapshot.signatureEvidence
  );
  return Boolean(
    contract.signatureCompletedAt ||
      contract.signedAt ||
      finalSnapshot.signedAt ||
      signatureEvidence.valid === true ||
      signatureEvidence.signatureHash
  );
};

const getContractSubmittedDate = (contract: UnknownRecord): string =>
  toSeoulDateKey(
    contract.submittedAt ||
      contract.createdAt ||
      asRecord(contract.invitationSnapshot).createdAt ||
      contract.completedAt ||
      asRecord(contract.finalSnapshot).completedAt
  );

const getContractCompletedDate = (contract: UnknownRecord): string =>
  toSeoulDateKey(contract.completedAt || asRecord(contract.finalSnapshot).completedAt);

type PaymentStatus =
  | "WAITING_CUSTOMER"
  | "APPROVED"
  | "PARTIALLY_CANCELED"
  | "CANCELED"
  | "FAILED"
  | "DRAFT"
  | "PROCESSING";

export const getDashboardPaymentStatus = (
  payment: UnknownRecord = {}
): PaymentStatus => {
  const status = String(payment.status || "").toUpperCase();
  const approvalStatus = String(payment.approvalStatus || "").toUpperCase();
  const executionStatus = String(payment.executionStatus || "").toUpperCase();
  const amount = pickFiniteAmount(payment.amount);
  const remainingAmount = Number(payment.remainingAmount ?? amount) || 0;
  const cancelledAmount = Number(payment.cancelledAmount) || 0;
  if (status === "CANCELED" || (cancelledAmount > 0 && remainingAmount <= 0)) {
    return "CANCELED";
  }
  if (status === "PARTIALLY_CANCELED" || cancelledAmount > 0) {
    return "PARTIALLY_CANCELED";
  }
  if (
    [status, approvalStatus, executionStatus].some((value) =>
      /PROCESSING|APPROVING|REGISTERING|DELETING|RECONCILIATION_REQUIRED|REVIEW_REQUIRED/.test(
        value
      )
    )
  ) {
    return "PROCESSING";
  }
  if (
    [status, approvalStatus, executionStatus].some((value) =>
      /FAILED|ERROR|REJECTED/.test(value)
    ) ||
    payment.errorCode ||
    payment.errorMessage
  ) {
    return "FAILED";
  }
  if (
    status === "APPROVED" ||
    approvalStatus === "APPROVED" ||
    pickString(payment.transactionId)
  ) {
    return "APPROVED";
  }
  if (
    ["STANDARD_READY", "LINK_READY"].includes(status) ||
    approvalStatus === "WAITING_CUSTOMER" ||
    (payment.requestType === "BUYLINK" && payment.paymentUrl)
  ) {
    return "WAITING_CUSTOMER";
  }
  if (
    ["DRAFT", "QUEUED", "NOT_STARTED", "PREFLIGHT_VALIDATED"].includes(status) ||
    (!status && !approvalStatus)
  ) {
    return "DRAFT";
  }
  return "PROCESSING";
};

// Approval evidence is independent from the request's current cancellation/error state.
// A transaction id is stronger evidence than approvalCode alone.
const isApprovedPayment = (payment: UnknownRecord): boolean =>
  Boolean(pickString(payment.transactionId));

const isIssuedLinkPayment = (payment: UnknownRecord, status: PaymentStatus): boolean =>
  LINK_PAYMENT_TYPES.has(String(payment.requestType || "").toUpperCase()) &&
  Boolean(
    payment.paymentUrl ||
      payment.linkRegisteredAt ||
      ["WAITING_CUSTOMER", "APPROVED", "PARTIALLY_CANCELED", "CANCELED"].includes(
        status
      )
  );

const getPaymentRequestedDate = (payment: UnknownRecord): string =>
  toSeoulDateKey(payment.createdAt);

const getPaymentApprovedDate = (payment: UnknownRecord): string =>
  toSeoulDateKey(payment.approvedAt || payment.paidAt);

const matchesDimension = (actual: string, selected: string): boolean =>
  selected === "ALL" || actual === selected;

const getContractOperationalStatus = (
  contract: UnknownRecord,
  asOfDateKey: string
): DashboardStatusFilter => {
  if (isContractError(contract)) return "ERROR";
  if (isCompletedContract(contract)) return "NORMAL";
  const submittedAge = ageInDays(getContractSubmittedDate(contract), asOfDateKey);
  if (submittedAge !== null && submittedAge >= 3) return "DELAYED";
  return "IN_PROGRESS";
};

const getPaymentOperationalStatus = (
  payment: UnknownRecord,
  status: PaymentStatus,
  asOfDateKey: string
): DashboardStatusFilter => {
  if (status === "FAILED") return "ERROR";
  if (status === "WAITING_CUSTOMER") {
    const age = ageInDays(
      toSeoulDateKey(payment.linkRegisteredAt || payment.createdAt),
      asOfDateKey
    );
    if (age !== null && age >= 8) return "DELAYED";
  }
  if (["WAITING_CUSTOMER", "DRAFT", "PROCESSING"].includes(status)) {
    return "IN_PROGRESS";
  }
  return "NORMAL";
};

const addStatusAmount = (
  target: Record<string, DashboardStatusAmount>,
  key: string,
  amount: number
) => {
  target[key].count += 1;
  target[key].amount += amount;
};

const buildFunnelStages = (
  values: Array<{ key: string; label: string; count: number }>
): DashboardFunnelStage[] =>
  values.map((stage, index) => {
    const previous = index > 0 ? values[index - 1].count : null;
    return {
      ...stage,
      previousConversionRate:
        previous === null || previous === 0
          ? null
          : Math.round((stage.count / previous) * 1000) / 10,
      dropOffCount: previous === null ? null : Math.max(0, previous - stage.count),
    };
  });

const buildTrendDates = (asOfDateKey: string): DashboardTrendRow[] =>
  Array.from({ length: 30 }, (_, index) => ({
    date: shiftDateKey(asOfDateKey, index - 29),
    contractCompletedCount: 0,
    paymentApprovedAmount: 0,
  }));

const getExceptionCategory = (
  code: string
): DashboardExceptionRow["category"] => {
  const normalized = code.toLowerCase();
  if (normalized === "permission-denied" || normalized === "failed-precondition") {
    return "SYSTEM";
  }
  if (/kcp|approval|card|transaction/.test(normalized)) return "KCP";
  if (/manager|assignment|미지정/.test(normalized)) return "SALES";
  return "DATA";
};

export const buildIntegratedDashboardStats = ({
  contracts = [],
  payments = [],
  filters,
  now = new Date(),
}: {
  contracts?: UnknownRecord[];
  payments?: UnknownRecord[];
  filters: DashboardFilters;
  now?: Date;
}): IntegratedDashboardStats => {
  const asOfDateKey = toSeoulDateKey(now);
  const summary: IntegratedDashboardStats["summary"] = {
    contractTargetCount: 0,
    contractCompletionRate: null,
    submittedContractCount: 0,
    completedContractCount: 0,
    todayCompletedContractCount: 0,
    stalledContractCount: 0,
    paymentRequestCount: 0,
    paymentRequestAmount: 0,
    paymentApprovedCount: 0,
    paymentApprovedAmount: 0,
    paymentCancelledAmount: 0,
    paymentNetApprovedAmount: 0,
    paymentOutstandingCount: 0,
    paymentOutstandingAmount: 0,
    paymentExpiringTodayCount: 0,
    paymentExpiringTodayAmount: 0,
    paymentExpiredCount: 0,
    paymentExpiredAmount: 0,
  };
  const contractStatus: Record<string, number> = {
    COMPLETED: 0,
    PENDING_COREP: 0,
    IN_PROGRESS: 0,
    REJECTED: 0,
    ERROR: 0,
  };
  const paymentStatus = Object.fromEntries(
    [
      "WAITING_CUSTOMER",
      "APPROVED",
      "PARTIALLY_CANCELED",
      "CANCELED",
      "FAILED",
      "DRAFT",
      "PROCESSING",
    ].map((key) => [key, { count: 0, amount: 0 }])
  ) as Record<string, DashboardStatusAmount>;
  const paymentAging = Object.fromEntries(
    ["TODAY", "2_3_DAYS", "4_7_DAYS", "8_PLUS_DAYS"].map((key) => [
      key,
      { count: 0, amount: 0 },
    ])
  ) as Record<string, DashboardStatusAmount>;
  const filterCorporations = new Set<string>();
  const filterManagers = new Set<string>();
  const filterParts = new Set<string>();
  const managerMap = new Map<string, DashboardManagerRow>();
  const exceptionMap = new Map<string, DashboardExceptionRow>();
  const trendRows = buildTrendDates(asOfDateKey);
  const trendMap = new Map(trendRows.map((row) => [row.date, row]));
  const contractIdentities = new Set<string>();
  const identityVerified = new Set<string>();
  const signed = new Set<string>();
  const completed = new Set<string>();
  const paymentRequested = new Set<string>();
  const paymentApproved = new Set<string>();
  const duplicateOutstanding = new Map<string, number>();
  const paymentSuppressedByPart = filters.part !== "ALL";
  let contractMissingBusinessNumberCount = 0;
  let paymentMissingBusinessNumberCount = 0;
  let amountInvariantMismatchCount = 0;
  let duplicatePaymentRequestIdCount = 0;
  let coRepresentativePendingCount = 0;
  let unassignedContractCount = 0;
  let repeatedPaymentFailureCount = 0;
  let paymentMissingExpiryCount = 0;

  const addException = (
    key: string,
    label: string,
    category = getExceptionCategory(key)
  ) => {
    const existing = exceptionMap.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }
    exceptionMap.set(key, { key, label, category, count: 1 });
  };

  const getManagerRow = (manager: string, part = "미지정") => {
    const existing = managerMap.get(manager);
    if (existing) {
      if (existing.part === "미지정" && part !== "미지정") existing.part = part;
      return existing;
    }
    const row: DashboardManagerRow = {
      manager,
      part,
      submittedContracts: 0,
      completedContracts: 0,
      paymentApprovedAmount: 0,
      paymentOutstandingAmount: 0,
      attentionCount: 0,
    };
    managerMap.set(manager, row);
    return row;
  };

  (Array.isArray(contracts) ? contracts : []).forEach((rawContract) => {
    const contract = asRecord(rawContract);
    if (isDeletedContract(contract)) return;
    const corporation = getContractCorporation(contract);
    const manager = getContractManager(contract);
    const part = getContractPart(contract);
    filterCorporations.add(corporation);
    filterManagers.add(manager);
    filterParts.add(part);
    if (
      !matchesDimension(corporation, filters.corporation) ||
      !matchesDimension(manager, filters.manager) ||
      !matchesDimension(part, filters.part)
    ) {
      return;
    }
    const operationalStatus = getContractOperationalStatus(contract, asOfDateKey);
    if (filters.status !== "ALL" && operationalStatus !== filters.status) return;

    const submittedDate = getContractSubmittedDate(contract);
    const completedDate = getContractCompletedDate(contract);
    const submittedInRange = isDateInRange(
      submittedDate,
      filters.dateFrom,
      filters.dateTo
    );
    const completedInRange = submittedInRange && isCompletedContract(contract);
    const managerRow = submittedInRange ? getManagerRow(manager, part) : null;
    if (submittedInRange) {
      summary.contractTargetCount += 1;
      summary.submittedContractCount += 1;
      managerRow!.submittedContracts += 1;
      if (manager === "미지정") unassignedContractCount += 1;
    }
    if (completedInRange) {
      summary.completedContractCount += 1;
      managerRow!.completedContracts += 1;
    }
    if (isCompletedContract(contract) && completedDate === asOfDateKey) {
      summary.todayCompletedContractCount += 1;
    }
    const isStalled =
      !isCompletedContract(contract) &&
      (ageInDays(submittedDate, asOfDateKey) ?? -1) >= 3;
    if (submittedInRange && isStalled) {
      summary.stalledContractCount += 1;
      managerRow!.attentionCount += 1;
    }
    if (submittedInRange) {
      if (isCompletedContract(contract)) contractStatus.COMPLETED += 1;
      else if (isPendingCoRepresentativeContract(contract)) {
        contractStatus.PENDING_COREP += 1;
        coRepresentativePendingCount += 1;
      } else if (isRejectedContract(contract)) {
        contractStatus.REJECTED += 1;
        addException("CONTRACT_REJECTED", "관리자 반려 계약", "SALES");
      } else if (isContractError(contract)) {
        contractStatus.ERROR += 1;
        addException(
          pickString(contract.errorCode, "CONTRACT_ERROR"),
          pickString(contract.errorCode, "계약 오류")
        );
      } else contractStatus.IN_PROGRESS += 1;
    }

    if (completedDate && trendMap.has(completedDate) && isCompletedContract(contract)) {
      trendMap.get(completedDate)!.contractCompletedCount += 1;
    }

    const businessNumber = getContractBusinessNumber(contract);
    if (!businessNumber) {
      if (submittedInRange || completedInRange) contractMissingBusinessNumberCount += 1;
      return;
    }
    if (submittedInRange) contractIdentities.add(businessNumber);
    if (submittedInRange && hasIdentityEvidence(contract)) {
      identityVerified.add(businessNumber);
    }
    if (submittedInRange && identityVerified.has(businessNumber) && hasSignatureEvidence(contract)) {
      signed.add(businessNumber);
    }
    if (submittedInRange && signed.has(businessNumber) && isCompletedContract(contract)) {
      completed.add(businessNumber);
    }
  });

  const seenPaymentIds = new Set<string>();
  (Array.isArray(payments) ? payments : []).forEach((rawPayment, index) => {
    const payment = asRecord(rawPayment);
    const paymentId = pickString(payment.paymentRequestId, payment.id, `row-${index}`);
    if (seenPaymentIds.has(paymentId)) {
      duplicatePaymentRequestIdCount += 1;
      return;
    }
    seenPaymentIds.add(paymentId);
    const requestType = String(payment.requestType || "").toUpperCase();
    if (!LINK_PAYMENT_TYPES.has(requestType)) return;
    const corporation = getPaymentCorporation(payment);
    const manager = getPaymentManager(payment);
    filterCorporations.add(corporation);
    filterManagers.add(manager);
    if (
      !matchesDimension(corporation, filters.corporation) ||
      !matchesDimension(manager, filters.manager) ||
      paymentSuppressedByPart
    ) {
      return;
    }
    const status = getDashboardPaymentStatus(payment);
    const operationalStatus = getPaymentOperationalStatus(payment, status, asOfDateKey);
    if (filters.status !== "ALL" && operationalStatus !== filters.status) return;

    const amount = pickFiniteAmount(payment.amount);
    const cancelledAmount = Math.max(0, Number(payment.cancelledAmount) || 0);
    const remainingAmount = Math.max(
      0,
      Number(payment.remainingAmount ?? amount) || 0
    );
    const requestedDate = getPaymentRequestedDate(payment);
    const approvedDate = getPaymentApprovedDate(payment);
    const requestInRange = isDateInRange(
      requestedDate,
      filters.dateFrom,
      filters.dateTo
    );
    const approvalInRange = isDateInRange(
      approvedDate,
      filters.dateFrom,
      filters.dateTo
    );
    const issued = isIssuedLinkPayment(payment, status);
    const approved = isApprovedPayment(payment);
    const activeBuylink =
      requestType === "BUYLINK" &&
      issued &&
      status === "WAITING_CUSTOMER" &&
      !approved;
    if (activeBuylink) {
      const expiryStatus = getBuyLinkExpiryStatus(
        payment.urlExpireDate,
        asOfDateKey
      );
      if (expiryStatus === "EXPIRING_TODAY") {
        summary.paymentExpiringTodayCount += 1;
        summary.paymentExpiringTodayAmount += amount;
      } else if (expiryStatus === "EXPIRED") {
        summary.paymentExpiredCount += 1;
        summary.paymentExpiredAmount += amount;
      } else if (expiryStatus === "UNKNOWN") {
        paymentMissingExpiryCount += 1;
      }
    }
    const managerRow =
      (issued && requestInRange) || (approved && approvalInRange)
        ? getManagerRow(manager)
        : null;
    if (issued && requestInRange) {
      summary.paymentRequestCount += 1;
      summary.paymentRequestAmount += amount;
      addStatusAmount(paymentStatus, status, amount);
      if (status === "WAITING_CUSTOMER") {
        summary.paymentOutstandingCount += 1;
        summary.paymentOutstandingAmount += amount;
        managerRow!.paymentOutstandingAmount += amount;
        const age = ageInDays(
          toSeoulDateKey(payment.linkRegisteredAt || payment.createdAt),
          asOfDateKey
        );
        const agingKey =
          age === null || age <= 1
            ? "TODAY"
            : age <= 3
            ? "2_3_DAYS"
            : age <= 7
            ? "4_7_DAYS"
            : "8_PLUS_DAYS";
        addStatusAmount(paymentAging, agingKey, amount);
        const businessNumber = getPaymentBusinessNumber(payment);
        const duplicateKey = `${businessNumber || payment.vendorId || payment.vendorCode}|${amount}`;
        duplicateOutstanding.set(
          duplicateKey,
          (duplicateOutstanding.get(duplicateKey) || 0) + 1
        );
      }
    }
    if (requestInRange && status === "FAILED") {
      if (managerRow) managerRow.attentionCount += 1;
      if (requestType === "STANDARD" && Number(payment.attemptCount) >= 2) {
        repeatedPaymentFailureCount += 1;
      }
      const code = pickString(payment.errorCode, "PAYMENT_FAILED");
      addException(code, code === "PAYMENT_FAILED" ? "결제 처리 실패" : code);
    }
    if (approved && approvalInRange) {
      summary.paymentApprovedCount += 1;
      summary.paymentApprovedAmount += amount;
      summary.paymentCancelledAmount += cancelledAmount;
      managerRow!.paymentApprovedAmount += amount - cancelledAmount;
    }
    if (approvedDate && trendMap.has(approvedDate) && approved) {
      trendMap.get(approvedDate)!.paymentApprovedAmount += amount - cancelledAmount;
    }
    if (
      approved &&
      Math.abs(amount - (cancelledAmount + remainingAmount)) > 1
    ) {
      amountInvariantMismatchCount += 1;
      addException("PAYMENT_AMOUNT_MISMATCH", "결제 금액 불변식 불일치", "DATA");
    }
    const businessNumber = getPaymentBusinessNumber(payment);
    if (!businessNumber) {
      if (requestInRange || approvalInRange) paymentMissingBusinessNumberCount += 1;
      return;
    }
    if (issued && requestInRange && completed.has(businessNumber)) {
      paymentRequested.add(businessNumber);
    }
    if (approved && approvalInRange && paymentRequested.has(businessNumber)) {
      paymentApproved.add(businessNumber);
    }
  });

  summary.contractCompletionRate =
    summary.contractTargetCount > 0
      ? Math.round(
          (summary.completedContractCount / summary.contractTargetCount) * 1000
        ) / 10
      : null;
  summary.paymentNetApprovedAmount =
    summary.paymentApprovedAmount - summary.paymentCancelledAmount;
  const duplicateOutstandingCount = Array.from(duplicateOutstanding.values()).reduce(
    (sum, count) => sum + (count > 1 ? count : 0),
    0
  );
  if (amountInvariantMismatchCount > 0) {
    // The exception row is populated above; keep the diagnostic count separate.
  }

  const actions: DashboardActionItem[] = [];
  const addAction = (item: DashboardActionItem) => {
    if (item.count > 0) actions.push(item);
  };
  addAction({
    key: "PAYMENT_BUYLINK_EXPIRED",
    severity: "URGENT",
    scope: "PAYMENT",
    title: "BUYLINK 유효기간 만료",
    count: summary.paymentExpiredCount,
    amount: summary.paymentExpiredAmount,
    detail: "저장된 KCP url_expire_dt가 오늘보다 이전인 미승인 BUYLINK입니다.",
  });
  addAction({
    key: "PAYMENT_BUYLINK_EXPIRING_TODAY",
    severity: "URGENT",
    scope: "PAYMENT",
    title: "오늘 만료 BUYLINK",
    count: summary.paymentExpiringTodayCount,
    amount: summary.paymentExpiringTodayAmount,
    detail: "저장된 KCP url_expire_dt가 오늘인 미승인 BUYLINK입니다.",
  });
  addAction({
    key: "PAYMENT_8_PLUS_DAYS",
    severity: "URGENT",
    scope: "PAYMENT",
    title: "8일 이상 미결제",
    count: paymentAging["8_PLUS_DAYS"].count,
    amount: paymentAging["8_PLUS_DAYS"].amount,
    detail: "링크 발급 후 경과일 기준이며 공식 BUYLINK 유효일자 지표와 별도입니다.",
  });
  addAction({
    key: "REPEATED_PAYMENT_FAILURE",
    severity: "URGENT",
    scope: "PAYMENT",
    title: "표준결제 반복 실패",
    count: repeatedPaymentFailureCount,
    amount: 0,
    detail: "STANDARD 실패 상태이면서 기록된 시도 횟수가 2회 이상인 요청입니다.",
  });
  addAction({
    key: "DUPLICATE_OUTSTANDING",
    severity: "WARNING",
    scope: "PAYMENT",
    title: "중복 요청 의심",
    count: duplicateOutstandingCount,
    amount: 0,
    detail: "동일 거래처 식별자와 동일 금액의 미결제 요청입니다.",
  });
  addAction({
    key: "PENDING_COREP",
    severity: "WARNING",
    scope: "CONTRACT",
    title: "공동대표 인증 대기",
    count: coRepresentativePendingCount,
    amount: 0,
    detail: "제출된 계약의 공동대표 워크플로 상태 기준입니다.",
  });
  addAction({
    key: "UNASSIGNED_MANAGER",
    severity: "WARNING",
    scope: "CONTRACT",
    title: "담당자 미지정",
    count: unassignedContractCount,
    amount: 0,
    detail: "선택 기간의 제출 계약 중 담당자 정보가 없는 건입니다.",
  });
  addAction({
    key: "PAYMENT_AMOUNT_MISMATCH",
    severity: "URGENT",
    scope: "PAYMENT",
    title: "결제 금액 확인 필요",
    count: amountInvariantMismatchCount,
    amount: 0,
    detail: "승인액과 취소액·잔액 합계가 일치하지 않는 건입니다.",
  });

  const funnel = buildFunnelStages([
    { key: "SUBMITTED", label: "전자공급계약서 거래처", count: contractIdentities.size },
    { key: "IDENTITY", label: "본인확인 증빙", count: identityVerified.size },
    { key: "SIGNED", label: "전자서명 증빙", count: signed.size },
    { key: "COMPLETED", label: "계약 완료", count: completed.size },
    { key: "PAYMENT_REQUESTED", label: "결제 링크 발급", count: paymentRequested.size },
    { key: "PAYMENT_APPROVED", label: "결제 승인", count: paymentApproved.size },
  ]);

  return {
    summary,
    contractStatus,
    paymentStatus,
    paymentAging,
    funnel,
    actions: actions.sort((left, right) => {
      const priority = { URGENT: 0, WARNING: 1, INFO: 2 };
      const expiryPriority: Record<string, number> = {
        PAYMENT_BUYLINK_EXPIRED: 0,
        PAYMENT_BUYLINK_EXPIRING_TODAY: 1,
      };
      return (
        (expiryPriority[left.key] ?? 10) - (expiryPriority[right.key] ?? 10) ||
        priority[left.severity] - priority[right.severity] ||
        right.count - left.count
      );
    }),
    managers: Array.from(managerMap.values()).sort(
      (left, right) =>
        right.attentionCount - left.attentionCount ||
        right.paymentOutstandingAmount - left.paymentOutstandingAmount ||
        left.manager.localeCompare(right.manager, "ko-KR")
    ),
    trends: trendRows,
    exceptions: Array.from(exceptionMap.values())
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
      .slice(0, 5),
    filterOptions: {
      corporations: Array.from(filterCorporations).sort((left, right) =>
        left.localeCompare(right, "ko-KR")
      ),
      managers: Array.from(filterManagers).sort((left, right) =>
        left.localeCompare(right, "ko-KR")
      ),
      parts: Array.from(filterParts).sort((left, right) =>
        left.localeCompare(right, "ko-KR")
      ),
    },
    diagnostics: {
      contractMissingBusinessNumberCount,
      paymentMissingBusinessNumberCount,
      amountInvariantMismatchCount,
      duplicatePaymentRequestIdCount,
      paymentMissingExpiryCount,
      paymentPartFilterAvailable: false,
    },
  };
};
